/**
 * Phone field logic: country detection, as-you-type formatting and strict validation.
 * The heavy lifting is libphonenumber, loaded on demand from `{base}/embed-phone.js` so forms
 * without a phone field never download it. Everything here takes the library as an argument,
 * which keeps it unit-testable without a browser.
 */

interface ParsedNumber {
  isValid(): boolean;
  number: string;
  country?: string;
}
interface Formatter {
  input(text: string): string;
  getCountry(): string | undefined;
  getCallingCode(): string | undefined;
}
export interface PhoneLib {
  AsYouType: new (country?: string) => Formatter;
  getCountries(): string[];
  getCountryCallingCode(country: string): string;
  parsePhoneNumberFromString(text: string, country?: string): ParsedNumber | undefined;
}

let pending: Promise<PhoneLib | null> | null = null;

/** Resolves to null when the script cannot load (blocked, offline); the server still validates. */
export function loadPhoneLib(base: string): Promise<PhoneLib | null> {
  if (pending) return pending;
  pending = new Promise((resolve) => {
    const w = window as unknown as { FFSPhone?: PhoneLib };
    if (w.FFSPhone) return resolve(w.FFSPhone);
    const s = document.createElement('script');
    s.src = `${base}/embed-phone.js`;
    s.async = true;
    s.onload = () => resolve(w.FFSPhone ?? null);
    s.onerror = () => {
      pending = null; // allow a retry on the next mount
      resolve(null);
    };
    document.head.appendChild(s);
  });
  return pending;
}

/** "US" → 🇺🇸. Systems without flag glyphs (Windows) show the two letters instead. */
export function flagEmoji(country: string): string {
  if (!/^[A-Za-z]{2}$/.test(country)) return '';
  return String.fromCodePoint(
    ...[...country.toUpperCase()].map((ch) => 0x1f1e6 + ch.charCodeAt(0) - 65),
  );
}

export interface CountryOption {
  code: string;
  name: string;
  dial: string;
}

export function countryOptions(lib: PhoneLib, nameOf: (code: string) => string): CountryOption[] {
  const all = lib.getCountries().map((code) => ({
    code,
    name: nameOf(code),
    dial: `+${lib.getCountryCallingCode(code)}`,
  }));
  all.sort((a, b) => a.name.localeCompare(b.name));
  // The US first: it is by far the most common pick for this audience.
  const us = all.findIndex((c) => c.code === 'US');
  if (us > 0) all.unshift(...all.splice(us, 1));
  return all;
}

/** Calling codes used by more than one country, and the country most numbers belong to. */
const MAIN_COUNTRY: Record<string, string> = {
  '1': 'US',
  '7': 'RU',
  '39': 'IT',
  '44': 'GB',
  '47': 'NO',
  '61': 'AU',
  '212': 'MA',
  '262': 'RE',
  '290': 'SH',
  '358': 'FI',
  '590': 'GP',
  '599': 'CW',
};

function mainCountryFor(
  lib: PhoneLib,
  callingCode: string | undefined,
  current: string,
): string | undefined {
  if (!callingCode) return undefined;
  // Keep the current pick when it already uses this code (a Canadian typing +1 stays Canadian).
  if (lib.getCountryCallingCode(current) === callingCode) return current;
  return (
    MAIN_COUNTRY[callingCode] ??
    lib.getCountries().find((c) => lib.getCountryCallingCode(c) === callingCode)
  );
}

/** Keep digits and one leading plus; everything else the person typed is decoration. */
function clean(text: string): string {
  const t = text.trim();
  return (t.startsWith('+') ? '+' : '') + t.replace(/\D/g, '');
}

/**
 * Format what has been typed so far. A leading "+" switches to international entry and the
 * country follows the number, which is what moves the flag as they type.
 */
export function formatPhone(
  lib: PhoneLib,
  text: string,
  country: string,
): { text: string; country: string } {
  const raw = clean(text);
  if (!raw) return { text: '', country };
  if (raw.startsWith('+')) {
    const f = new lib.AsYouType();
    const out = f.input(raw);
    // Codes shared by several countries (+1, +44, +61) stay ambiguous for a few digits; show the
    // code's main country until the number itself settles it.
    const detected = f.getCountry() ?? mainCountryFor(lib, f.getCallingCode(), country);
    return { text: out, country: detected ?? country };
  }
  return { text: new lib.AsYouType(country).input(raw), country };
}

/** The number in E.164 ("+16155551234") when it is a real number for its country, else null. */
export function toE164(lib: PhoneLib, text: string, country: string): string | null {
  const raw = clean(text);
  if (!raw) return null;
  const parsed = lib.parsePhoneNumberFromString(raw, country);
  return parsed?.isValid() ? parsed.number : null;
}

/** Without the library: only reject what cannot be a phone number in any country. */
export function looksLikePhone(text: string): boolean {
  const digits = text.replace(/\D/g, '');
  return digits.length >= 7 && digits.length <= 15 && !/[A-Za-z]/.test(text);
}
